import { useCallback, useEffect, useRef } from "react";
import { useSoundContext } from "@/providers/SoundContext";

// Hero audio scrub (prototype): the soundtrack is baked into the hero video
// and ducks out through a closing lowpass as the text scroll passes the fade
// window. Constants in docs/digital-exhibit-motion-spec.md.
// Pushed later (was 0.6→0.8) so the soundtrack carries further into the
// scroll-away, matching the prototype (QA: ours faded out a little sooner).
const AUDIO_FADE_START = 0.7;
const AUDIO_FADE_END = 0.9;
const LOWPASS_OPEN = 22050;
const LOWPASS_CLOSED = 200;

type ChainMode = "unset" | "webaudio" | "volume";

// Returns an apply(t) the scroll loop calls per frame with the smoothed text
// progress. Builds a MediaElementSource -> lowpass -> gain chain lazily (on
// first sound-enabled frame); falls back to plain element volume when the
// chain can't carry audio (no AudioContext, or CORS-silenced media).
export function useHeroScrubAudio(videoRef: React.RefObject<HTMLVideoElement>) {
  const { soundEnabled } = useSoundContext();
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;

  const chain = useRef<{
    ctx: AudioContext | null;
    filter: BiquadFilterNode | null;
    gain: GainNode | null;
    mode: ChainMode;
  }>({ ctx: null, filter: null, gain: null, mode: "unset" });
  const lastT = useRef(0);

  const initChain = useCallback(() => {
    const el = videoRef.current;
    const state = chain.current;
    if (!el || state.mode !== "unset") return;
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = ctx.createMediaElementSource(el);
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = LOWPASS_OPEN;
      filter.Q.value = 0.7;
      const gain = ctx.createGain();
      gain.gain.value = 1;
      source.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      state.ctx = ctx;
      state.filter = filter;
      state.gain = gain;
      state.mode = "webaudio";
      // Cross-origin media without CORS headers routes silence through the
      // graph — detect it and fall back to plain element volume control
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      gain.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      setTimeout(() => {
        if (chain.current.mode !== "webaudio" || el.paused || el.muted) return;
        analyser.getByteTimeDomainData(data);
        const silent = data.every((v) => v === 128 || v === 0);
        if (silent && lastT.current < AUDIO_FADE_START) {
          state.gain?.disconnect();
          state.mode = "volume";
        }
      }, 1500);
    } catch {
      state.mode = "volume";
    }
  }, [videoRef]);

  const apply = useCallback(
    (t: number) => {
      lastT.current = t;
      const el = videoRef.current;
      if (!el) return;
      if (!soundEnabledRef.current) {
        el.muted = true;
        return;
      }
      // Past the fade window the soundtrack is inaudible, but an unmuted
      // element plus a running AudioContext still hold the iOS audio session —
      // and iOS grants it to one owner, so anything further down the page (the
      // Music Explorer's <audio>) gets paused by the platform when it tries to
      // claim it. Ducking to gain 0 isn't enough; release both.
      if (t >= AUDIO_FADE_END) {
        if (!el.muted) el.muted = true;
        const ctx = chain.current.ctx;
        if (ctx?.state === "running") ctx.suspend().catch(() => {});
        return;
      }
      // Build the chain BEFORE unmuting: between muted=false and
      // createMediaElementSource the element plays raw at full volume — an
      // audible blip on iOS the first time sound is enabled
      const state = chain.current;
      if (state.mode === "unset") initChain();
      if (state.ctx?.state === "suspended") state.ctx.resume();
      const firstUnmute = el.muted;
      el.muted = false;

      let gainValue = 1;
      let freq = LOWPASS_OPEN;
      if (t >= AUDIO_FADE_END) {
        gainValue = 0;
        freq = LOWPASS_CLOSED;
      } else if (t > AUDIO_FADE_START) {
        const k = (t - AUDIO_FADE_START) / (AUDIO_FADE_END - AUDIO_FADE_START);
        gainValue = Math.max(0, 1 - k);
        freq = LOWPASS_OPEN * (LOWPASS_CLOSED / LOWPASS_OPEN) ** k;
      }
      if (state.mode === "webaudio" && state.gain && state.filter) {
        if (firstUnmute) {
          // Ease the soundtrack in on unmute rather than cutting to level
          // mid-track (the per-frame writes below converge after the ramp)
          const now = state.gain.context.currentTime;
          state.gain.gain.cancelScheduledValues(now);
          state.gain.gain.setValueAtTime(0, now);
          state.gain.gain.linearRampToValueAtTime(gainValue, now + 0.4);
        } else {
          state.gain.gain.value = gainValue;
        }
        state.filter.frequency.value = freq;
        el.volume = 1;
      } else {
        el.volume = gainValue;
      }
    },
    [initChain, videoRef]
  );

  // Re-apply gating when the global toggle changes
  useEffect(() => {
    apply(lastT.current);
  }, [soundEnabled, apply]);

  useEffect(
    () => () => {
      chain.current.ctx?.close().catch(() => {});
    },
    []
  );

  return apply;
}
